import React, { useState, useMemo } from 'react';
import Table from '../../components/Table/Table';
import CodePrefixForm from '../../components/Form/CodePrefixForm';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import { CodePrefix } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useCodePrefixes, useCreateCodePrefix, useUpdateCodePrefix, useDeleteCodePrefix } from '../../hooks/useCodePrefixes';
import { useToast } from '../../contexts/ToastContext';
import { nextCode } from '../../utils/codePrefixUtils';

const CodePrefixes: React.FC = () => {
  const { showToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingPrefix, setEditingPrefix] = useState<CodePrefix | undefined>();
  const [searchTerm, setSearchTerm] = useState('');

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 1000 }
  }), []);

  const { data: codePrefixes = [], isLoading, error, refetch } = useCodePrefixes(queryOptions);

  const createMutation = useCreateCodePrefix(() => {
    showToast('Code prefix created successfully', 'success');
    refetch();
    setShowForm(false);
    setEditingPrefix(undefined);
  });

  const updateMutation = useUpdateCodePrefix(() => {
    showToast('Code prefix updated successfully', 'success');
    refetch();
    setShowForm(false);
    setEditingPrefix(undefined);
  });

  const deleteMutation = useDeleteCodePrefix(() => {
    showToast('Code prefix deleted successfully', 'success');
    refetch();
  });

  const handleAddPrefix = async (data: Omit<CodePrefix, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      await createMutation.execute(data);
    } catch (err) {
      console.error('Create code prefix error:', err);
    }
  };

  const handleEditPrefix = async (data: Omit<CodePrefix, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (editingPrefix) {
      try {
        const updatedData = { ...editingPrefix, ...data };
        await updateMutation.execute({ id: editingPrefix.id, data: updatedData });
      } catch (err) {
        console.error('Update code prefix error:', err);
      }
    }
  };

  const handleDeletePrefix = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this code prefix?')) {
      try {
        const prefix = codePrefixes.find(p => p.id === id);
        if (prefix) await deleteMutation.execute(prefix);
      } catch (err) {
        console.error('Delete code prefix error:', err);
      }
    }
  };

  const handleEdit = (prefix: CodePrefix) => {
    setEditingPrefix(prefix);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingPrefix(undefined);
  };

  const filteredPrefixes = codePrefixes.filter(prefix =>
    prefix.prefix.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prefix.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleExport = () => {
    const exportData = filteredPrefixes.map(prefix => ({
      'Prefix': prefix.prefix,
      'Description': prefix.description,
      'Current Sequence': prefix.currentSequence,
      'Status': prefix.isActive ? 'Active' : 'Inactive',
      'Created': new Date(prefix.createdAt).toLocaleDateString()
    }));
    exportToExcel(exportData, `code_prefixes_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredPrefixes.map(prefix => ({
      'Prefix': prefix.prefix,
      'Description': prefix.description,
      'Current Sequence': prefix.currentSequence,
      'Status': prefix.isActive ? 'Active' : 'Inactive',
      'Created': new Date(prefix.createdAt).toLocaleDateString()
    }));
    printAsExcel(exportData, 'Code Prefixes');
  };

  const filterConfigs: never[] = [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading code prefixes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <ErrorState message="Error loading code prefixes" onRetry={() => refetch()} entityName="code prefixes" />
      </div>
    );
  }

  const columns = [
    {
      key: 'prefix',
      label: 'Prefix Code',
      sortable: true,
      render: (value: string) => <span className="font-bold text-lg text-ocean-600 font-mono">{value}</span>
    },
    {
      key: 'description',
      label: 'Description',
      sortable: true,
      render: (value: string) => <span className="font-medium text-gray-900">{value}</span>
    },
    {
      key: 'currentSequence',
      label: 'Next Sequence',
      sortable: true,
      render: (value: number) => <span className="font-mono text-gray-700">{Math.max(1, value || 1).toString().padStart(2, '0')}</span>
    },
    {
      key: 'nextFormNumber',
      label: 'Next Generated Code',
      render: (_: any, item: CodePrefix) => (
        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{nextCode(item)}</span>
      )
    },
    {
      key: 'isActive',
      label: 'Status',
      render: (value: boolean) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: any, item: CodePrefix) => (
        <div className="flex space-x-2">
          <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-900 font-medium">Edit</button>
          <button onClick={() => handleDeletePrefix(item.id)} className="text-red-600 hover:text-red-900 font-medium">Delete</button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Requisition Code Prefixes"
        description="Manage prefixes for requisition form numbers"
      />

      {showForm && (
        <div className="card p-6">
          <h2 className="text-xl font-semibold mb-4">{editingPrefix ? 'Edit Code Prefix' : 'Add New Code Prefix'}</h2>
          <CodePrefixForm
            onSubmit={editingPrefix ? handleEditPrefix : handleAddPrefix}
            initialData={editingPrefix}
            onCancel={handleCancel}
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="text-sm text-gray-600">Total Prefixes</div>
          <div className="text-2xl font-bold text-gray-900">{codePrefixes.length}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-600">Active Prefixes</div>
          <div className="text-2xl font-bold text-green-600">{codePrefixes.filter(p => p.isActive).length}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-600">Inactive Prefixes</div>
          <div className="text-2xl font-bold text-red-600">{codePrefixes.filter(p => !p.isActive).length}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-600">Total Requisitions Generated</div>
          <div className="text-2xl font-bold text-ocean-600">{codePrefixes.reduce((sum, p) => sum + p.currentSequence, 0)}</div>
        </div>
      </div>

      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search by prefix code or description..."
        filters={filterConfigs}
        onClearFilters={() => setSearchTerm('')}
        totalCount={codePrefixes.length}
        filteredCount={filteredPrefixes.length}
        entityName="prefixes"
        onAdd={!showForm ? () => setShowForm(true) : undefined}
        addLabel="Add Prefix"
        onExport={handleExport}
        onPrint={handlePrint}
      />

      <div className="card overflow-hidden">
        <Table data={filteredPrefixes} columns={columns} />
      </div>
    </div>
  );
};

export default CodePrefixes;
